import {
  ChangeDetectionStrategy,
  Component,
  Inject,
  OnInit,
} from '@angular/core';
import { Router } from '@angular/router';
import { SpacingValue } from '@frk/eds-components';
import { AbstractBaseComponent } from '@shared/abstract-base/abstract-base.component';
import { LinkService } from '@services/link.service';
import { WidenService } from '@services/widen.service';
import { AppStateService } from '@services';
import { InteractiveMediaCardContentDTO } from './interactive-media-cards-grid.interface';
import { DOCUMENT } from '@angular/common';

import { Logger } from '@utils/logger';
import { ViewModeService } from '@services/view-mode.service';
/**
 * Logger
 */
const logger = Logger.getLogger('InteractiveMediaCardGridComponent');

/**
 * The Interactive media card grid component can be used to display
 * collection of Intrective media cards in a grid
 *
 * Selector: `ft-interactive-media-cards-grid`
 *
 * Exported as: `InteractiveMediaCardGridComponent`
 */
@Component({
  selector: 'ft-interactive-media-cards-grid',
  templateUrl: './interactive-media-cards-grid.component.html',
})
export class InteractiveMediaCardGridComponent
  extends AbstractBaseComponent
  implements OnInit {
  interactiveMediaCards: Array<InteractiveMediaCardContentDTO> = [];

  isAnimationOffFlag = false;

  private COUNT_TO_STRIP_HTML = 290;

  /**
   * Card theme
   */
  cardTheme: string;

  /**
   * Hint message will display on top of grid
   */
  hintMessage: string;

  /**
   * Constructor
   */
  constructor(
    private appStateService: AppStateService,
    private linkService: LinkService,
    private widenService: WidenService,
    @Inject(DOCUMENT) private documentRef: Document,
    private viewModeService: ViewModeService
  ) {
    super();
    this.isEditMode = this.viewModeService.isEditMode();
  }

  /**
   * On initialisation
   */
  ngOnInit() {
    super.ngOnInit();
    this.interactiveMediaCards = this.getContent();
    this.hintMessage = this.getHintMessage();
    this.cardTheme = this.getTheme();
  }

  /**
   * Read the content from the document
   */
  getContent(): Array<InteractiveMediaCardContentDTO> {
    const content: Array<InteractiveMediaCardContentDTO> = this.document?.getData<
      Array<InteractiveMediaCardContentDTO>
    >().interactiveMediaCardCompound;
    this.hideAnimation = this.component?.getParameters()?.hideAnimation;

    if (content?.length) {
      content.forEach((interactiveCard) => {
        if (interactiveCard.titleLink?.linkCollection?.length) {
          interactiveCard.titleLink =
            interactiveCard.titleLink.linkCollection[0];
          interactiveCard.titleLink.url = this.linkService.getCTALink(
            this.page,
            interactiveCard.titleLink,
            this.appStateService?.getHomePageUrl()
          );
        }

        if (interactiveCard.backCardLink?.linkCollection?.length) {
          interactiveCard.backCardLink =
            interactiveCard.backCardLink.linkCollection[0];
          interactiveCard.backCardLink.url = this.linkService.getCTALink(
            this.page,
            interactiveCard.backCardLink,
            this.appStateService?.getHomePageUrl()
          );
        }

        if (interactiveCard.frontCardImage) {
          interactiveCard.frontCardImageUrl = this.widenService.getWidenAssetUrl(
            interactiveCard.frontCardImage?.widenDocument
          );
        }

        if (interactiveCard.cardType === 'image') {
          interactiveCard.imgFallbackSrc = '/assets/images/ben_head.png';
        }
        if (interactiveCard.frontCardQuestion) {
          interactiveCard.strippedContentFrontCardQuestion = this.stripHTMLToLength(
            interactiveCard?.frontCardQuestion
          );
        }
        if (interactiveCard.backCardContent) {
          interactiveCard.strippedContentBackCardContent = this.stripHTMLToLength(
            interactiveCard?.backCardContent
          );
        }
      });
    }
    return content;
  }

  /**
   * Get cards theme
   */
  getTheme(): string {
    const cardTheme = this.component.getParameters()?.theme;
    return cardTheme === 'primary' ? 'page-theme' : 'white';
  }

  /**
   * Get hint message
   */
  getHintMessage(): string {
    return this.component.getParameters()?.hint;
  }

  /**
   * This is related to WDE-3695
   * This method is used to strip off the rendered innerHTML to specific length.
   * Earlier slice pipe was directly used to strip the html but this also sliced the
   * html tags used.The length of the content included the HTML tags as well
   * @param content HTML Content
   * @param lengthToStrip Length to which visible content need to be sliced
   * @returns HTML Content
   */
  private stripHTMLToLength(
    content: string,
    lengthToStrip: number = this.COUNT_TO_STRIP_HTML
  ): string {
    const pElement = this.documentRef.createElement('p');
    const updatedPElement = this.documentRef.createElement('p');
    pElement.innerHTML = content;
    let totalLength = 0;
    // Iterate the Child nodes and check for the length of already rendered text
    pElement.childNodes.forEach((childNode) => {
      const clonedNode = childNode.cloneNode(true);
      // If text length is greater than length required,
      // do not add element to Copied Content
      if (clonedNode.textContent && totalLength < lengthToStrip) {
        const updatedLength = totalLength + clonedNode.textContent.length;
        if (updatedLength > lengthToStrip) {
          const newText = clonedNode.textContent.slice(
            0,
            lengthToStrip - totalLength
          );
          clonedNode.textContent = newText;
        }
        totalLength += clonedNode.textContent.length;
        updatedPElement.appendChild(clonedNode);
      } else if (!clonedNode.textContent) {
        // Add nodes not having innerText like </br>
        updatedPElement.appendChild(clonedNode);
      }
    });
    return updatedPElement.innerHTML;
  }

  /**
   * Set the status of animation flag
   */
  setAnimationFlagStatus(status: boolean): void {
    this.isAnimationOffFlag = status;
  }
}
